import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

const StoreSuccessPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [state, setState] = useState('checking'); // checking, success, pending, error
  const [message, setMessage] = useState('Confirming your payment...');
  const [txType, setTxType] = useState('product');

  const poll = useCallback(async (sessionId, attempt) => {
    if (attempt >= MAX_ATTEMPTS) {
      setState('pending');
      setMessage('Payment is still processing. Check your email or orders shortly.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/store/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setTxType(data.transaction_type || 'product');
        if (data.payment_status === 'paid') {
          setState('success');
          setMessage(data.transaction_type === 'tip' ? 'Your tip was sent. Thank you for the support!' : 'Payment successful! Your order is confirmed.');
          return;
        }
        if (data.status === 'expired') {
          setState('error');
          setMessage('This checkout session expired. Please try again.');
          return;
        }
      }
      setState('checking');
      setMessage('Payment is being processed...');
      setTimeout(() => poll(sessionId, attempt + 1), POLL_INTERVAL);
    } catch (e) {
      setTimeout(() => poll(sessionId, attempt + 1), POLL_INTERVAL);
    }
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState('error');
      setMessage('No payment session found.');
      return;
    }
    poll(sessionId, 0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6" data-testid="store-success-page">
      <div className="w-full max-w-md bg-[#12101A] rounded-3xl border border-white/10 p-8 text-center">
        {state === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-[#00F0FF] animate-spin" />
            <h1 className="text-2xl font-bold text-white mt-6" style={{ fontFamily: 'Unbounded' }}>Processing</h1>
            <p className="text-[#A1A1AA] mt-2" data-testid="store-success-message">{message}</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#39FF14]" />
            <h1 className="text-2xl font-bold text-white mt-6" style={{ fontFamily: 'Unbounded' }} data-testid="store-success-title">
              {txType === 'tip' ? 'Tip Sent!' : 'Order Confirmed!'}
            </h1>
            <p className="text-[#A1A1AA] mt-2" data-testid="store-success-message">{message}</p>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-[#FF007F]" />
            <h1 className="text-2xl font-bold text-white mt-6" style={{ fontFamily: 'Unbounded' }}>Almost There</h1>
            <p className="text-[#A1A1AA] mt-2" data-testid="store-success-message">{message}</p>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-500" />
            <h1 className="text-2xl font-bold text-white mt-6" style={{ fontFamily: 'Unbounded' }}>Payment Issue</h1>
            <p className="text-[#A1A1AA] mt-2" data-testid="store-success-message">{message}</p>
          </>
        )}
        <button
          onClick={() => navigate('/')}
          className="mt-8 w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="store-success-home-btn"
        >
          <Home className="w-4 h-4" /> Back to Home
        </button>
      </div>
    </div>
  );
};

export default StoreSuccessPage;
